<template>
  <div>
    <EventModal
      v-model:visible-event-modal="eventModalVisible"
      :event="ihMaintenance"
      :event-type="EventType.IHMaintenance"
      :title="
        modalTitle ??
        (!!event
          ? t('components.issueModal.updateMaintenance').toString()
          : t('components.issueModal.newMaintenance').toString())
      "
      :save-and-new-button-title="
        !!event
          ? t('components.issueModal.updateAndTask').toString()
          : t('components.issueModal.createAndTask').toString()
      "
      :hide-add-task-button="hideAddTaskButton"
      :loading="loading"
      :original-owner-id="event?.owner?.id"
      :set-visible="setVisible"
      @save="saveIHMaintenance"
      @select-technical-place="setTechnicalPlace"
      @select-technical-category="setTechnicalCategory"
      @observer:valid="observerValid = $event"
    />
    <CreateTaskModal
      v-model:visible-action-modal="taskModalVisible"
      :event="ihMaintenance"
      @save="taskModalVisible = false"
      @hide="taskModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { provide, ref, nextTick, type PropType, computed, inject } from "vue";
import {
  useCreateIHMaintenanceMutation,
  useUpdateIHMaintenanceMutation,
  EventType,
  type IEventOption,
  EisenhowerPriority,
} from "@/graphql";
import EventModal from "@/components/event-action/event/modal/EventModal.vue";
import { CreateInjectionKey } from "@/components/event-action/issue/IssueTypes";
import CreateTaskModal from "@/components/event-action/task/modal/CreateTaskModal.vue";
import {
  BoardIdInjectionKey,
  DefaultEisenhowerPriorityInjectionKey,
  DefaultFlagInjectionKey,
  TechnicalPlaceForEventModalInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import { addEvent } from "@/graphql/apolloCacheUtils";
import { useSpecifiedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import type { IHMaintenanceEditType } from "@/components/event-action/event/modal/EventEditType";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  event: {
    type: Object as PropType<IHMaintenanceEditType | null>,
    default: null,
  },
  tileFlags: {
    type: Array,
    default: null,
  },
  hideAddTaskButton: {
    type: Boolean,
    default: false,
  },
  setVisible: {
    type: Boolean,
    default: false,
  },

  modalTitle: {
    type: String,
    required: false,
    default: undefined,
  },
});
const emit = defineEmits(["createEvent", "updateEvent", "save"]);
const observerValid = ref(false);

const injectedBoardId = inject(BoardIdInjectionKey, ref<number | null>(null));

const boardId = computed(() => {
  const id =
    props.event?.boardConnection?.find((con) => con.status === 0)?.boardId ?? injectedBoardId.value;
  if (id == null) {
    return -1; // when modal is open on board change the board is is for a split second null and that would collide and i dont know any better solution right no
  }
  return id;
});
const { board } = useSpecifiedBoardContext(boardId);

const eventModalVisible = ref(props.visible);
const taskModalVisible = ref(false);
const defaultFlags = inject(DefaultFlagInjectionKey, ref([]));
const technicalPlaceIdForEventModal = inject(TechnicalPlaceForEventModalInjectionKey, null);

const defaultTechnicalCategoryId = computed(() => {
  return technicalPlaceIdForEventModal?.value.id ?? board?.value?.technicalPlaceId;
});

const defaultEisenhowerPriority = inject(
  DefaultEisenhowerPriorityInjectionKey,
  ref(EisenhowerPriority.NO_PRIORITY),
);

function defaultIHMaintenance(): IHMaintenanceEditType {
  return {
    technicalCategoryId: defaultTechnicalCategoryId.value,
    technicalPlaceId: undefined,
    description: "",
    internalResourcesIds: [],
    equipmentsIds: [],
    plannedTime: 0,
    repTime: [],
    flags: defaultFlags.value,
    fileIds: [],
    eisenhowerPriority: defaultEisenhowerPriority.value,
  };
}

const ihMaintenance: any = ref(
  props.event ? JSON.parse(JSON.stringify(props.event)) : defaultIHMaintenance(),
);

provide(
  CreateInjectionKey,
  computed(() => props?.event?.id == null),
);

const { mutate: createIHMaintenanceMutation, loading: loadingCreate } =
  useCreateIHMaintenanceMutation({});
const { mutate: updateIHMaintenanceMutation, loading: loadingUpdate } =
  useUpdateIHMaintenanceMutation({});
const loading = computed(() => loadingCreate.value || loadingUpdate.value);
const createIHMaintenance = async (openTask: boolean) => {
  if (observerValid.value !== true) {
    return;
  }
  if ("document" in ihMaintenance.value) {
    delete ihMaintenance.value.document;
  }

  if (
    !ihMaintenance.value.technicalPlaceId ||
    !ihMaintenance.value.technicalCategoryId ||
    !board?.value?.siteId
  ) {
    throw new Error("technicalPlaceId and technicalCategoryId are required");
  }
  const res = await createIHMaintenanceMutation(
    {
      ihMaintenance: {
        ...ihMaintenance.value,
        flags: ihMaintenance.value.flags.map((flag: any) => ({
          id: flag.id,
        })),
      },
      boardId: board.value.id,
      siteId: board.value.siteId,
    },
    {
      update: (_cache, { data }) => {
        if (data && board?.value) {
          addEvent(data.CreateIHMaintenance, board.value.id);
        }
      },
    },
  );

  if (res?.data?.CreateIHMaintenance == null) {
    throw Error("invalid response");
  }
  emit("save", {
    eventId: res.data.CreateIHMaintenance.id,
    eventType: EventType.IHMaintenance,
  });
  await nextTick();
  eventModalVisible.value = false;
  if (openTask === true) {
    if (res.data?.CreateIHMaintenance != null) {
      ihMaintenance.value = res.data.CreateIHMaintenance;
    }
    taskModalVisible.value = true;
  }
  const eventIdentifier: IEventOption = {
    eventId: res.data.CreateIHMaintenance.id,
    eventType: EventType.IHMaintenance,
  };
  emit("createEvent", eventIdentifier, openTask);
};
const updateIHMaintenance = async (openTask: boolean) => {
  if (observerValid.value !== true || props.event?.id == null) {
    return;
  }

  await updateIHMaintenanceMutation({
    ihMaintenanceId: props.event.id,
    ihMaintenance: {
      technicalPlaceId: ihMaintenance.value.technicalPlaceId!,
      technicalCategoryId: ihMaintenance.value.technicalCategoryId!,
      internalResourcesIds: ihMaintenance.value.internalResourcesIds,
      equipmentsIds: ihMaintenance.value.equipmentsIds,
      plannedTime: ihMaintenance.value.plannedTime,
      repTime: ihMaintenance.value.repTime,
      description: ihMaintenance.value.description,
      flags: ihMaintenance.value.flags.map((flag: any) => ({
        id: flag.id,
      })),
      fileIds: ihMaintenance.value.fileIds,
      eisenhowerPriority: ihMaintenance.value.eisenhowerPriority,
    },
  });
  eventModalVisible.value = false;
  if (openTask === true) {
    await nextTick();
    taskModalVisible.value = true;
  }
  const eventIdentifier: IEventOption = {
    eventId: props.event.id,
    eventType: EventType.IHMaintenance,
  };
  emit("updateEvent", eventIdentifier, openTask);
};
const saveIHMaintenance = computed(() => {
  return props.event ? updateIHMaintenance : createIHMaintenance;
});
const show = () => {
  if (props.event === null) {
    ihMaintenance.value = defaultIHMaintenance();
  }
  eventModalVisible.value = true;
};

const setTechnicalCategory = (value: number) => {
  ihMaintenance.value.technicalCategoryId = value;
};
const setTechnicalPlace = (value: number) => {
  ihMaintenance.value.technicalPlaceId = value;
};

defineExpose({
  show,
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
